import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { authApi } from '../services/api';

interface UnverifiedUserBannerProps {
  email: string;
}

export default function UnverifiedUserBanner({ email }: UnverifiedUserBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');

  const handleResend = async () => {
    setResending(true);
    setMessage('');

    try {
      await authApi.resendVerification(email);
      setMessage('Verification email sent! Check your inbox.');
    } catch {
      setMessage('Failed to resend. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  if (dismissed) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Email verification required.</span>{' '}
                Please check your inbox and verify your email address to access all features.
              </p>
              {message && (
                <p className={`text-xs mt-1 ${message.includes('sent') ? 'text-primary-dark' : 'text-red-700'}`}>
                  {message}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3 ml-4">
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-sm font-medium text-yellow-700 hover:text-yellow-800 underline disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {resending ? 'Sending...' : 'Resend Email'}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="text-yellow-600 hover:text-yellow-800"
              aria-label="Dismiss notification"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
